import React from 'react';
import { Button, Group, Modal, NumberInput, Select, Stack, Textarea } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IStepMedication, TimeOfDay } from '../../../global/entities/OTP/OTP';
import { IDoseSuggestionResult } from '../../../global/entities/MME/MME';

interface IDrugRouteOption {
    value: string;
    label: string;
    drugID: number;
    routeID: number;
    unitName: string;
}

interface IMedicationEditModalProps {
    opened: boolean;
    onClose: () => void;
    stepNumber: number;
    medication?: IStepMedication;
    suggestion?: IDoseSuggestionResult;
    drugRouteOptions: IDrugRouteOption[];
    globalDrugRouteKey?: string | null;
    onSave: (medication: IStepMedication) => void;
    onDelete?: () => void;
    isNew?: boolean;
}

const generateMedicationId = (): string => {
    return `med_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const MedicationEditModal: React.FC<IMedicationEditModalProps> = ({
    opened,
    onClose,
    stepNumber,
    medication,
    suggestion,
    drugRouteOptions,
    globalDrugRouteKey,
    onSave,
    onDelete,
    isNew = false,
}) => {
    const { t } = useTranslation();

    // Get the default drug/route key from medication or global selection
    const getDefaultDrugRouteKey = React.useCallback((): string | null => {
        if (medication?.drugID && medication?.routeID)
            return `${medication.drugID}-${medication.routeID}`;

        return globalDrugRouteKey ?? null;
    }, [medication?.drugID, medication?.routeID, globalDrugRouteKey]);

    const [selectedDrugRouteKey, setSelectedDrugRouteKey] = React.useState<string | null>(
        getDefaultDrugRouteKey()
    );
    const [dosageStrength, setDosageStrength] = React.useState<number>(
        medication?.dosageStrength ?? suggestion?.dosageStrength ?? 0
    );
    const [frequencyPerDay, setFrequencyPerDay] = React.useState<number>(
        medication?.frequencyPerDay ?? suggestion?.frequencyPerDay ?? 1
    );
    const [timeOfDay, setTimeOfDay] = React.useState<TimeOfDay | null>(
        medication?.timeOfDay ?? null
    );
    const [notes, setNotes] = React.useState<string>(medication?.notes ?? '');

    // Reset form values when medication changes
    React.useEffect(() => {
        setSelectedDrugRouteKey(getDefaultDrugRouteKey());
        setDosageStrength(medication?.dosageStrength ?? suggestion?.dosageStrength ?? 0);
        setFrequencyPerDay(medication?.frequencyPerDay ?? suggestion?.frequencyPerDay ?? 1);
        setTimeOfDay(medication?.timeOfDay ?? null);
        setNotes(medication?.notes ?? '');
    }, [medication, suggestion, getDefaultDrugRouteKey]);

    const selectedDrugOption = React.useMemo(() => {
        if (!selectedDrugRouteKey) return null;
        return drugRouteOptions.find((opt) => opt.value === selectedDrugRouteKey);
    }, [selectedDrugRouteKey, drugRouteOptions]);

    const frequencyOptions = [
        { value: '1', label: t('otp.stepsTable.freqOnceDaily', '1x/day') },
        { value: '2', label: t('otp.stepsTable.freqTwiceDaily', '2x/day') },
        { value: '3', label: t('otp.stepsTable.freqThriceDaily', '3x/day') },
        { value: '4', label: t('otp.stepsTable.freqFourDaily', '4x/day') },
    ];

    const timeOfDayOptions = [
        { value: 'morning', label: t('otp.stepsTable.timeOfDay.morning', 'Morning') },
        { value: 'afternoon', label: t('otp.stepsTable.timeOfDay.afternoon', 'Afternoon') },
        { value: 'evening', label: t('otp.stepsTable.timeOfDay.evening', 'Evening') },
        { value: 'night', label: t('otp.stepsTable.timeOfDay.night', 'Night') },
    ];

    const handleSave = () => {
        if (!selectedDrugOption) return;

        const updatedMedication: IStepMedication = {
            id: medication?.id ?? generateMedicationId(),
            drugID: selectedDrugOption.drugID,
            routeID: selectedDrugOption.routeID,
            drugName: selectedDrugOption.label,
            unitName: selectedDrugOption.unitName,
            dosageStrength,
            frequencyPerDay,
            conversionFactor: suggestion?.conversionFactor ?? medication?.conversionFactor,
            timeOfDay: timeOfDay ?? undefined,
            notes: notes || undefined,
        };

        onSave(updatedMedication);
        onClose();
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete();
            onClose();
        }
    };

    const modalTitle = isNew
        ? t('otp.stepsTable.addMedication', 'Add Medication - Step {{step}}', { step: stepNumber })
        : t('otp.stepsTable.editMedication', 'Edit Medication - Step {{step}}', { step: stepNumber });

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={modalTitle}
            size="md"
        >
            <Stack gap="md">
                <Select
                    label={t('otp.stepsTable.drug', 'Drug')}
                    description={t('otp.stepsTable.selectDrugDesc', 'Select the medication for this step')}
                    value={selectedDrugRouteKey}
                    onChange={(val) => setSelectedDrugRouteKey(val)}
                    data={drugRouteOptions}
                    placeholder={t('otp.stepsTable.selectDrug', 'Select drug')}
                    searchable
                    required
                />

                {selectedDrugOption && (
                    <>
                        <NumberInput
                            label={t('otp.stepsTable.dosageStrength', 'Dosage')}
                            description={t(
                                'otp.stepsTable.dosageStrengthDesc',
                                'Enter any dosage value'
                            )}
                            value={dosageStrength}
                            onChange={(val) => setDosageStrength(Number(val) || 0)}
                            min={0}
                            step={0.5}
                            decimalScale={2}
                            suffix={selectedDrugOption.unitName ? ` ${selectedDrugOption.unitName}` : ''}
                        />

                        <Group grow>
                            <Select
                                label={t('otp.stepsTable.frequency', 'Frequency')}
                                value={String(frequencyPerDay)}
                                onChange={(val) => setFrequencyPerDay(Number(val) || 1)}
                                data={frequencyOptions}
                            />

                            <Select
                                label={t('otp.stepsTable.timeOfDayLabel', 'Time of Day')}
                                description={t('otp.stepsTable.timeOfDayDesc', 'Optional')}
                                value={timeOfDay}
                                onChange={(val) => setTimeOfDay(val as TimeOfDay | null)}
                                data={timeOfDayOptions}
                                placeholder={t('otp.stepsTable.anyTime', 'Any time')}
                                clearable
                            />
                        </Group>

                        <Textarea
                            label={t('otp.stepsTable.notes', 'Notes')}
                            placeholder={t(
                                'otp.stepsTable.notesPlaceholder',
                                'Add instructions or notes...'
                            )}
                            value={notes}
                            onChange={(e) => setNotes(e.currentTarget.value)}
                            minRows={2}
                            maxRows={4}
                        />
                    </>
                )}

                <Group justify="space-between" mt="md">
                    <Group>
                        {!isNew && onDelete && (
                            <Button variant="subtle" color="red" onClick={handleDelete}>
                                {t('button.delete', 'Delete')}
                            </Button>
                        )}
                    </Group>
                    <Group>
                        <Button variant="subtle" onClick={onClose}>
                            {t('button.cancel', 'Cancel')}
                        </Button>
                        <Button onClick={handleSave} disabled={!selectedDrugOption}>
                            {t('button.save', 'Save')}
                        </Button>
                    </Group>
                </Group>
            </Stack>
        </Modal>
    );
};

export default MedicationEditModal;
